import { useEffect, useState } from "react";
import axios from "axios";
import { BsStarFill } from "react-icons/bs";
import { MdCheck, MdClose, MdOutlineFlag, MdOutlineCheckCircle } from "react-icons/md";

// ── Étoiles ──────────────────────────────────────────────────────
function StarRow({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <BsStarFill key={i} className={i <= rating ? "text-amber-400" : "text-gray-200"} size={12} />
      ))}
    </div>
  );
}

// ── Card avis réutilisable ────────────────────────────────────────
function ReviewCard({ review, actions }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 flex flex-col sm:flex-row sm:items-start gap-4 hover:shadow-sm transition">
      <div className="flex-1 min-w-0">
        {/* Hôtel + ref */}
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-sm font-semibold text-gray-800">
            {review.hotelId?.name ?? "—"}
          </span>
          <span className="text-xs text-gray-400">
            Réf : {review.reservationId?.reservationId ?? "—"}
          </span>
          {/* Badge signalement hôtelier */}
          {review.hotelFlag?.flaggedAt && !review.hotelFlag?.reviewed && (
            <span className="text-xs bg-orange-50 text-orange-500 border border-orange-100 px-2 py-0.5 rounded-full">
              Signalé par l'hôtel · {review.hotelFlag.reason}
            </span>
          )}
        </div>

        {/* Auteur */}
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-2 flex-wrap">
          <span>{review.userId?.name} {review.userId?.surname}</span>
          <span>·</span>
          <span>{review.userId?.email}</span>
          <span>·</span>
          <span>
            {new Date(review.createdAt).toLocaleDateString("fr-FR", {
              day: "numeric", month: "short", year: "numeric",
            })}
          </span>
        </div>

        {/* Séjour */}
        {review.reservationId?.checkInDate && (
          <p className="text-xs text-gray-400 mb-2">
            Séjour : {new Date(review.reservationId.checkInDate).toLocaleDateString("fr-FR")}
            {" → "}
            {new Date(review.reservationId.checkOutDate).toLocaleDateString("fr-FR")}
            {review.reservationId.totalNights && ` · ${review.reservationId.totalNights} nuits`}
          </p>
        )}

        <StarRow rating={review.rating} />

        {review.comment ? (
          <p className="mt-2 text-sm text-gray-600 leading-relaxed">{review.comment}</p>
        ) : (
          <p className="mt-2 text-xs text-gray-300 italic">Sans commentaire</p>
        )}

        {/* Réponse hôtel existante */}
        {review.hotelReply?.text && (
          <div className="mt-3 pl-3 border-l-2 border-gray-200">
            <p className="text-xs text-gray-400 mb-0.5">Réponse de l'hôtel</p>
            <p className="text-sm text-gray-600">{review.hotelReply.text}</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex sm:flex-col gap-2 shrink-0">
        {actions}
      </div>
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────
const TABS = [
  { key: "pending",  label: "En attente" },
  { key: "approved", label: "Approuvés"  },
  { key: "flagged",  label: "Signalés"   },
];

export default function ReviewsModeration() {
  const [tab, setTab]             = useState("pending");
  const [reviews, setReviews]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]         = useState(0);
  const [actionLoading, setActionLoading] = useState(null);
  const [flagReason, setFlagReason] = useState({}); // { [id]: reason }

  const fetchReviews = async (t = tab, p = 1) => {
    try {
      setLoading(true);
      let url, params = { page: p };

      if (t === "pending") {
        url = "https://api.komorotel.com/api/reviews/admin/pending";
      } else if (t === "approved") {
        url = "https://api.komorotel.com/api/reviews/admin/approved";
      } else {
        url = "https://api.komorotel.com/api/reviews/admin/approved";
        params.flaggedOnly = true;
      }

      const res = await axios.get(url, { params, withCredentials: true });
      setReviews(res.data.reviews);
      setTotalPages(res.data.totalPages);
      setTotal(res.data.total);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchReviews(tab, 1);
  }, [tab]);

  useEffect(() => {
    fetchReviews(tab, page);
  }, [page]);

  // ── Actions ───────────────────────────────────────────────────
  const moderate = async (id, action) => {
    try {
      setActionLoading(id);
      await axios.put(
        `https://api.komorotel.com/api/reviews/${id}/moderate`,
        { action },
        { withCredentials: true }
      );
      setReviews((prev) => prev.filter((r) => r._id !== id));
      setTotal((prev) => prev - 1);
    } catch (err) {
      alert(err.response?.data?.message || "Erreur");
    } finally {
      setActionLoading(null);
    }
  };

  const flag = async (id) => {
    const reason = flagReason[id];
    if (!reason) return alert("Choisissez une raison");
    try {
      setActionLoading(id);
      await axios.put(
        `https://api.komorotel.com/api/reviews/${id}/flag`,
        { reason },
        { withCredentials: true }
      );
      setReviews((prev) => prev.filter((r) => r._id !== id));
      setTotal((prev) => prev - 1);
    } catch (err) {
      alert(err.response?.data?.message || "Erreur");
    } finally {
      setActionLoading(null);
    }
  };

  const dismissFlag = async (id) => {
    try {
      setActionLoading(id);
      await axios.put(
        `https://api.komorotel.com/api/reviews/${id}/dismiss-flag`,
        {},
        { withCredentials: true }
      );
      setReviews((prev) => prev.filter((r) => r._id !== id));
      setTotal((prev) => prev - 1);
    } catch (err) {
      alert(err.response?.data?.message || "Erreur");
    } finally {
      setActionLoading(null);
    }
  };

  // ── Render actions par onglet ─────────────────────────────────
  const renderActions = (review) => {
    const isLoading = actionLoading === review._id;

    if (tab === "pending") {
      return (
        <>
          <button
            onClick={() => moderate(review._id, "approve")}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-green-50 text-green-600 text-sm font-medium hover:bg-green-100 disabled:opacity-50 transition"
          >
            <MdCheck size={16} /> Approuver
          </button>
          <button
            onClick={() => moderate(review._id, "reject")}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-50 text-red-500 text-sm font-medium hover:bg-red-100 disabled:opacity-50 transition"
          >
            <MdClose size={16} /> Rejeter
          </button>
        </>
      );
    }

    if (tab === "approved") {
      return (
        <div className="flex flex-col gap-2">
          <select
            value={flagReason[review._id] || ""}
            onChange={(e) =>
              setFlagReason((prev) => ({ ...prev, [review._id]: e.target.value }))
            }
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 focus:outline-none"
          >
            <option value="">Raison...</option>
            <option value="insultes">Insultes</option>
            <option value="spam">Spam</option>
            <option value="faux">Faux avis</option>
            <option value="inapproprié">Inapproprié</option>
            <option value="autre">Autre</option>
          </select>
          <button
            onClick={() => flag(review._id)}
            disabled={isLoading || !flagReason[review._id]}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-50 text-red-500 text-sm font-medium hover:bg-red-100 disabled:opacity-50 transition"
          >
            <MdOutlineFlag size={16} /> Retirer
          </button>
        </div>
      );
    }

    if (tab === "flagged") {
      return (
        <>
          <button
            onClick={() => dismissFlag(review._id)}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-green-50 text-green-600 text-sm font-medium hover:bg-green-100 disabled:opacity-50 transition"
          >
            <MdOutlineCheckCircle size={16} /> Garder
          </button>
          <div className="flex flex-col gap-2">
            <select
              value={flagReason[review._id] || ""}
              onChange={(e) =>
                setFlagReason((prev) => ({ ...prev, [review._id]: e.target.value }))
              }
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 focus:outline-none"
            >
              <option value="">Raison...</option>
              <option value="insultes">Insultes</option>
              <option value="spam">Spam</option>
              <option value="faux">Faux avis</option>
              <option value="inapproprié">Inapproprié</option>
              <option value="autre">Autre</option>
            </select>
            <button
              onClick={() => flag(review._id)}
              disabled={isLoading || !flagReason[review._id]}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-50 text-red-500 text-sm font-medium hover:bg-red-100 disabled:opacity-50 transition"
            >
              <MdOutlineFlag size={16} /> Retirer
            </button>
          </div>
        </>
      );
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Modération des avis</h1>
        <p className="text-sm text-gray-400 mt-0.5">{total} avis dans cette vue</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6 w-fit">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              tab === t.key
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex flex-col gap-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      )}

      {/* Vide */}
      {!loading && reviews.length === 0 && (
        <div className="bg-white rounded-2xl border p-12 text-center">
          <p className="text-gray-400 text-sm">
            {tab === "pending"  && "✅ Aucun avis en attente"}
            {tab === "approved" && "Aucun avis approuvé"}
            {tab === "flagged"  && "✅ Aucun signalement en attente"}
          </p>
        </div>
      )}

      {/* Liste */}
      {!loading && reviews.length > 0 && (
        <div className="flex flex-col gap-4">
          {reviews.map((review) => (
            <ReviewCard
              key={review._id}
              review={review}
              actions={renderActions(review)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-8">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:border-gray-400 disabled:opacity-30 transition"
          >
            ← Précédent
          </button>
          <span className="text-sm text-gray-500">{page} / {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:border-gray-400 disabled:opacity-30 transition"
          >
            Suivant →
          </button>
        </div>
      )}
    </div>
  );
}